/* eslint-disable no-param-reassign */

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { AutocompleteSkill } from 'src/components/skillAutocomplete/types/index';
import type { CompetitionLeaderboardResponse } from 'src/openapi';
import {
  fetchLeaderboard,
  fetchSkillsLeaders,
  followUser,
  unfollowUser,
} from '../actions/leaderboard';
import type { LeaderboardState } from '../types/leaderboard';

const leaderboardDetails: CompetitionLeaderboardResponse = {
  curr_user_score_details: undefined,
  rank_list: [],
  current_page: 0,
  page_size: 10,
  total_pages: 1,
};

const initialState: LeaderboardState = {
  leaderboardDetails,
  leaders: undefined,
  isLeaderboardLoading: false,
  isLeadersLoading: false,
  isUpdatingLeaderboard: false,
  selectedSkill: null,
  isUpdatingFollowRequest: false,
};

const leaderboardSlice = createSlice({
  name: 'leaderboard',
  initialState,
  reducers: {
    setLeaderboardDetails: (state, action) => {
      state.leaderboardDetails = action.payload;
    },
    setIsLeaderboardLoading: (state, action) => {
      state.isLeaderboardLoading = action.payload;
    },
    setIsUpdatingLeaderboard: (state, action) => {
      state.isUpdatingLeaderboard = action.payload;
    },
    setSelectedSkill: (
      state,
      action: PayloadAction<AutocompleteSkill | null>,
    ) => {
      state.selectedSkill = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLeaderboard.fulfilled, (state, action) => {
      state.leaderboardDetails = action.payload;
    });

    builder
      .addCase(fetchSkillsLeaders.pending, (state) => {
        state.isLeadersLoading = true;
      })
      .addCase(fetchSkillsLeaders.fulfilled, (state, action) => {
        state.leaders = action.payload.rank_list;
        state.isLeadersLoading = false;
      })
      .addCase(fetchSkillsLeaders.rejected, (state) => {
        state.isLeadersLoading = false;
      });

    builder
      .addCase(followUser.pending, (state) => {
        state.isUpdatingFollowRequest = true;
      })
      .addCase(followUser.fulfilled, (state, action) => {
        const {
          payload: { updated_followed_list: followerList },
          meta: { arg: id },
        } = action;

        if (!id) return;

        const isFollowed = followerList?.includes(id);

        state?.leaderboardDetails?.rank_list?.forEach((item) => {
          if (item?.user?.id === id) {
            item.user.is_followed = isFollowed;
          }
        });
        state?.leaders?.forEach((item) => {
          if (item?.user?.id === id) {
            item.user.is_followed = isFollowed;
          }
        });

        state.isUpdatingFollowRequest = false;
      })
      .addCase(followUser.rejected, (state) => {
        state.isUpdatingFollowRequest = false;
      });

    builder
      .addCase(unfollowUser.pending, (state) => {
        state.isUpdatingFollowRequest = true;
      })
      .addCase(unfollowUser.fulfilled, (state, action) => {
        const {
          payload: { updated_followed_list: followerList },
          meta: { arg: id },
        } = action;

        if (!id) return;

        const isFollowed = followerList?.includes(id);

        state?.leaderboardDetails?.rank_list?.forEach((item) => {
          if (item?.user?.id === id) {
            item.user.is_followed = isFollowed;
          }
        });
        state?.leaders?.forEach((item) => {
          if (item?.user?.id === id) {
            item.user.is_followed = isFollowed;
          }
        });
        state.isUpdatingFollowRequest = false;
      })
      .addCase(unfollowUser.rejected, (state) => {
        state.isUpdatingFollowRequest = false;
      });
  },
});

export const {
  setLeaderboardDetails,
  setIsLeaderboardLoading,
  setSelectedSkill,
  setIsUpdatingLeaderboard,
} = leaderboardSlice.actions;

export default leaderboardSlice.reducer;
